"use client";

import type { ClassValue } from "clsx";
import { CldImage, type CldImageProps } from "next-cloudinary";
import React from "react";
import Wrapper from "@/components/ui/layout/wrapper";
import { Skeleton } from "@/components/ui/skeleton";
import { cls, cn } from "@/lib/utils";

export interface ImageProperties extends CldImageProps {
  showError?: boolean;
  showSkeleton?: boolean;
  skeletonClassName?: ClassValue;
  wrapperClassName?: ClassValue;
  wrapperStyle?: React.CSSProperties;
  getError?: (error: string) => void;
}

const Image = React.forwardRef<HTMLImageElement, ImageProperties>(
  (
    {
      src,
      alt = "Image",
      width,
      height,
      fill = false,
      sizes = "100vw",
      showSkeleton = true,
      skeletonClassName = "",
      wrapperClassName = "",
      wrapperStyle = {},
      getError,
      onLoad,
      ...properties
    },
    reference,
  ) => {
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [imageError, setImageError] = React.useState<string | null>(null);

    const handleOnError = React.useCallback(() => {
      setImageError("Failed to load image");

      if (typeof getError === "function") {
        getError("Failed to load image");
      }
    }, [getError]);

    const handleOnLoaded = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (typeof onLoad === "function") {
          onLoad(event);
        }

        setIsLoaded(true);
        setImageError(null);
      },
      [onLoad],
    );

    if (imageError) {
      return null;
    }

    return (
      <Wrapper
        id="img-wrapper"
        className={cn(cls`w-full h-full relative`, wrapperClassName)}
        style={wrapperStyle}
      >
        <React.Fragment>
          {/* Skeleton Loader */}
          {!isLoaded && showSkeleton && (
            <Skeleton
              className={cn(
                cls`absolute inset-0 w-full h-full`,
                skeletonClassName,
              )}
            />
          )}

          {/* Motion-enabled Cloudinary Image */}
          {src && (
            <CldImage
              src={src}
              alt={alt}
              ref={reference}
              width={!fill ? width : undefined}
              height={!fill ? height : undefined}
              fill={fill}
              sizes={sizes}
              quality={80}
              onError={handleOnError}
              onLoad={handleOnLoaded}
              {...properties}
              style={{
                aspectRatio: "4/3",
                objectFit: "contain",
                ...properties.style,
              }}
            />
          )}
        </React.Fragment>
      </Wrapper>
    );
  },
);

Image.displayName = "Image";

export default Image;
